import React, {useMemo, useRef, useState} from 'react';
import {
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    IDetailsListProps,
    Icon,
    Link,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import PanelWrapper from '../Common/PanelWrapper';
import CurrencyDisplay from '../Currency/CurrencyDisplay';
import SuggestionReasonChip from './SuggestionReasonChip';
import {useGetStorefrontProductSuggestionsQuery} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {IStorefrontSuggestedBundle} from '../../models/Storefront';
import {bundleAvailability, bundleDetailLine, bundleSuggestionReason, BUNDLE_PANEL_SIZE} from './suggestedBundlesLogic';
import {suggestionAmountState} from './suggestedProductsLogic';

interface ISuggestedBundlesPanelProps {
    storefront: string;
    /** Bundles the strip already knows landed on the store, from this panel or from a card. */
    addedIds: string[];
    /** An add is in flight somewhere, so nothing here should start another one. */
    busy: boolean;
    /** Opens the strip's single shared bundle editor on this bundle. */
    onOpenBundle: (bundleId: string) => void;
    /**
     * Lists the chosen bundles and resolves with the ids that actually landed. The strip owns this so the
     * toast, the added bookkeeping and the grid refresh stay in one place.
     */
    onAddSelected: (bundleIds: string[]) => Promise<string[]>;
    onDismiss: () => void;
}

/**
 * A cell that holds something clickable.
 *
 * DetailsList wraps its rows in a SelectionZone, which reads clicks and key presses anywhere in the row as
 * "select this row". A link would therefore toggle the checkbox on its way to opening the editor.
 * `data-selection-disabled` is the opt-out Fluent honours, and the stopped handlers cover the paths it does not.
 */
const InteractiveCell: React.FC<{title?: string}> = ({title, children}) => (
    <div
        data-selection-disabled='true'
        title={title}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
    >
        {children}
    </div>
);

/**
 * The full bundle suggestion list, with what each bundle costs and what this store would charge for it.
 *
 * Its own read of the suggestions, because the strip deliberately asks for only a handful. Everything about
 * what has been added and what is in flight belongs to the strip and arrives as props, so a bundle listed
 * from in here also leaves the strip behind the panel.
 *
 * No markup column, unlike the products panel: a bundle carries its own pricing rather than inheriting a
 * per-listing markup, so there would be nothing for one to override.
 */
const SuggestedBundlesPanel: React.FC<ISuggestedBundlesPanelProps> = (props) => {
    const theme = useTheme();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    // The panel mounts only when opened, and the answer changes as bundles land on the store, so a cached
    // copy from the previous open is exactly what we do not want.
    const suggestions = useGetStorefrontProductSuggestionsQuery(
        // size 0 asks the API to run no product passes at all: this panel only reads bundles.
        {storefront: props.storefront, size: 0, bundleSize: BUNDLE_PANEL_SIZE},
        {refetchOnMountOrArgChange: true}
    );
    const bundles = useMemo(() => suggestions.data?.bundles ?? [], [suggestions.data]);

    // Fluent reads the selection prop in its constructor and never again, so this is built once and handed
    // over from the very first render. getKey keyed on the bundle id is what lets a selection survive the
    // items array being rebuilt underneath it.
    const selectionRef = useRef<Selection>();
    if (!selectionRef.current) {
        const created: Selection = new Selection({
            getKey: (item) => (item as IStorefrontSuggestedBundle).id,
            onSelectionChanged: () => {
                setSelectedIds((created.getSelection() as IStorefrontSuggestedBundle[]).map((b) => b.id));
            },
        });
        selectionRef.current = created;
    }
    const selection = selectionRef.current;

    // Already-listed bundles stay in the table with an Added marker in place of their controls, so a re-tick
    // of one of them must not be counted or sent.
    const addableIds = selectedIds.filter((id) => !props.addedIds.includes(id));

    const addSelected = async () => {
        if (addableIds.length === 0 || props.busy) return;
        const landed = await props.onAddSelected(addableIds);
        if (landed.length === 0) return;
        // Only what landed is cleared. Anything that failed stays ticked, so a second attempt is one click.
        landed.forEach((id) => selection.setKeySelected(id, false, false));
    };

    const columns: IColumn[] = [
        {
            key: 'thumbnail',
            name: '',
            minWidth: 40,
            maxWidth: 40,
            onRender: (bundle: IStorefrontSuggestedBundle) => bundle.imageUrl ? (
                <img
                    src={bundle.imageUrl}
                    alt=''
                    style={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        border: `1px solid ${theme.palette.neutralLight}`,
                        borderRadius: 2,
                    }}
                />
            ) : null,
        },
        {
            key: 'bundle',
            name: 'Bundle',
            minWidth: 220,
            isMultiline: true,
            onRender: (bundle: IStorefrontSuggestedBundle) => {
                const detail = bundleDetailLine(bundle);
                return (
                    <Stack styles={{root: {minWidth: 0}}}>
                        <InteractiveCell title={bundle.name}>
                            <Link onClick={() => props.onOpenBundle(bundle.id)}>
                                {bundle.name ?? 'Untitled'}
                            </Link>
                        </InteractiveCell>
                        {detail && (
                            <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                                {detail}
                            </Text>
                        )}
                    </Stack>
                );
            },
        },
        {
            // A column of its own rather than another line under the bundle name: the reasons line up and can
            // be read down the table, which is the point of showing them at all in a mixed list.
            key: 'reason',
            name: 'Suggested Because',
            minWidth: 120,
            maxWidth: 150,
            onRender: (bundle: IStorefrontSuggestedBundle) => (
                <SuggestionReasonChip reason={bundleSuggestionReason(bundle)}/>
            ),
        },
        {
            // The same dot and word as the card, not a filled badge. Amber is the third state: not out of
            // stock, but unpriceable for this store, with the failing part numbers on hover.
            key: 'stock',
            name: 'Stock',
            minWidth: 110,
            maxWidth: 130,
            onRender: (bundle: IStorefrontSuggestedBundle) => {
                const availability = bundleAvailability(bundle);
                const dot = availability.kind === 'inStock' ? theme.palette.green
                    : availability.kind === 'unpriced' ? theme.palette.orange : theme.palette.red;
                return (
                    <Stack horizontal verticalAlign='center' tokens={{childrenGap: 4}} title={availability.tooltip}>
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                flexShrink: 0,
                                borderRadius: '50%',
                                background: dot,
                            }}
                        />
                        <Text variant='small' nowrap styles={{root: {color: theme.palette.neutralSecondary}}}>
                            {availability.label}
                        </Text>
                    </Stack>
                );
            },
        },
        {
            key: 'cost',
            name: 'Cost',
            minWidth: 100,
            maxWidth: 120,
            onRender: (bundle: IStorefrontSuggestedBundle) => {
                if (suggestionAmountState(bundle.cost, undefined) !== 'value') {
                    return (
                        <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                            No cost
                        </Text>
                    );
                }
                return <Text variant='medium'><CurrencyDisplay value={bundle.cost}/></Text>;
            },
        },
        {
            key: 'price',
            name: 'Price',
            minWidth: 90,
            maxWidth: 110,
            onRender: (bundle: IStorefrontSuggestedBundle) => {
                if (suggestionAmountState(bundle.price, undefined) !== 'value') {
                    return (
                        <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                            No price
                        </Text>
                    );
                }
                return <Text variant='medium'><CurrencyDisplay value={bundle.price}/></Text>;
            },
        },
        {
            key: 'added',
            name: '',
            minWidth: 70,
            maxWidth: 80,
            onRender: (bundle: IStorefrontSuggestedBundle) => props.addedIds.includes(bundle.id) ? (
                <Text variant='small' styles={{root: {color: theme.palette.themePrimary}}}>
                    <Icon iconName='CheckMark' styles={{root: {marginRight: 4}}}/>
                    Added
                </Text>
            ) : null,
        },
    ];

    // Two lines of text in the name cell do not fit the default row height.
    const onRenderRow: IDetailsListProps['onRenderRow'] = (rowProps, defaultRender) => {
        if (!rowProps || !defaultRender) return null;
        return defaultRender({...rowProps, styles: {root: {minHeight: 56}, fields: {alignItems: 'center'}}});
    };

    const footerButtons = [
        <PrimaryButton
            key='add'
            text={props.busy ? 'Adding...' : `Add Selected (${addableIds.length})`}
            iconProps={props.busy ? undefined : {iconName: 'Add'}}
            disabled={addableIds.length === 0 || props.busy}
            onClick={addSelected}
        />,
        <DefaultButton key='close' text='Close' onClick={props.onDismiss}/>,
    ];

    return (
        <PanelWrapper
            isOpen={true}
            onDismiss={props.onDismiss}
            title='Suggested Bundles'
            type={PanelType.large}
            isFooterAtBottom
            footerButtons={footerButtons}
        >
            <Stack tokens={{childrenGap: '1em'}} styles={{root: {padding: '1em 0'}}}>
                <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                    Bundles this store is likely missing. Tick what belongs on it, then add them together.
                </Text>

                {suggestions.isLoading && <Spinner size={SpinnerSize.medium} label='Finding bundles...'/>}

                {!suggestions.isLoading && suggestions.isError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        We could not load suggestions right now.
                    </MessageBar>
                )}

                {!suggestions.isLoading && !suggestions.isError && bundles.length === 0 && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        There is nothing left to suggest. Every bundle we found is already on this store.
                    </MessageBar>
                )}

                {bundles.length > 0 && (
                    <DetailsList
                        items={bundles}
                        columns={columns}
                        selection={selection}
                        selectionMode={SelectionMode.multiple}
                        selectionPreservedOnEmptyClick
                        layoutMode={DetailsListLayoutMode.justified}
                        setKey='suggestedBundles'
                        getKey={(item: IStorefrontSuggestedBundle) => item.id}
                        onRenderRow={onRenderRow}
                    />
                )}
            </Stack>
        </PanelWrapper>
    );
};

export default SuggestedBundlesPanel;
